import React from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '../../components/PublicLayout';
import { 
  Wrench, Clock, DollarSign, Shield, CheckCircle, 
  Smartphone, Star, Calendar, FileText, ArrowRight, Check
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

export default function ContractorsOverview() {
  const steps = [
    {
      title: 'Apply to Join',
      description: 'Complete our application with your qualifications, insurance, and trade details.'
    },
    {
      title: 'Verification',
      description: 'We verify your credentials, insurance, and check for any issues.'
    },
    {
      title: 'Get Approved',
      description: 'Once approved, you\'re added to our network and can receive jobs.'
    },
    {
      title: 'Receive Job Alerts',
      description: 'Get notified instantly when relevant jobs are posted in your area.'
    },
    {
      title: 'First-to-Accept',
      description: 'Review job details and accept. First suitable contractor wins the job.'
    },
    {
      title: 'Complete & Get Paid',
      description: 'Do the work, upload evidence, submit invoice, get paid within 14 days.'
    }
  ];

  const benefits = [
    {
      icon: Wrench,
      title: 'Steady Work Stream',
      description: 'Access a constant flow of jobs from our landlord network. No more quiet periods.'
    },
    {
      icon: Clock,
      title: 'Fast Job Matching',
      description: 'Jobs go to the first suitable contractor to accept. Speed matters.'
    },
    {
      icon: DollarSign,
      title: 'Fair Pricing',
      description: 'You set your rates. No race to the bottom. Quality work is valued.'
    },
    {
      icon: Shield,
      title: 'Payment Guarantee',
      description: 'Approved work is paid within 14 days. No chasing landlords for payment.'
    },
    {
      icon: Smartphone,
      title: 'Mobile App',
      description: 'Manage everything from your phone: jobs, quotes, invoices, communication.'
    },
    {
      icon: Star,
      title: 'Build Reputation',
      description: 'Good reviews lead to more jobs. Top-rated contractors get priority.'
    },
    {
      icon: Calendar,
      title: 'Flexible Schedule',
      description: 'Only accept jobs that fit your availability. You\'re in control.'
    },
    {
      icon: FileText,
      title: 'Simple Admin',
      description: 'Quotes, invoices, and paperwork handled through the platform.'
    }
  ];

  const faqs = [
    {
      question: 'How do I join the network?',
      answer: 'Click "Apply Now", complete the application with your trade details, upload your insurance and certifications, and we\'ll review within 3-5 business days.'
    },
    {
      question: 'What trades do you need?',
      answer: 'All property-related trades: plumbers, electricians, gas engineers, locksmiths, builders, decorators, cleaners, gardeners, handymen, and more.'
    },
    {
      question: 'How much does it cost to join?',
      answer: 'Joining is free. We take a small commission (10-15%) on completed jobs. You set your rates, so factor this in.'
    },
    {
      question: 'How quickly do I get paid?',
      answer: 'Once work is approved (tenant confirms completion), payment is processed within 14 days to your registered bank account.'
    }
  ];

  const subPages = [
    { href: '/contractors/getting-started', title: 'Getting Started', description: 'Sign up & vetting', icon: CheckCircle },
    { href: '/contractors/jobs', title: 'Jobs & Scheduling', description: 'How jobs work', icon: Calendar },
    { href: '/contractors/payments', title: 'Payments', description: 'Quotes & invoicing', icon: DollarSign },
  ];

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-slate-50 py-12 sm:py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-6">
              <Wrench className="w-4 h-4" />
              For Contractors
            </div>
            <h1 className="text-4xl md:text-5xl font-display font-extrabold leading-tight mb-6">
              Grow Your Trade
              <span className="text-orange-600 block">Business With Us</span>
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Join our network of vetted contractors. Get steady work, fair pay, and guaranteed payments from landlords across the UK.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contractor/register">
                <Button size="lg" className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700">
                  Apply to Join
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contractors/getting-started">
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sub-page Navigation */}
      <section className="py-8 sm:py-12 bg-white border-y">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-3 gap-3 sm:gap-6">
            {subPages.map((page) => (
              <Link
                key={page.href}
                to={page.href}
                className="group p-4 sm:p-6 rounded-xl border hover:border-orange-500 hover:shadow-md transition-all text-center"
              >
                <page.icon className="w-8 h-8 mx-auto mb-3 text-slate-400 group-hover:text-orange-600 transition-colors" />
                <div className="font-semibold text-sm sm:text-base group-hover:text-orange-600 transition-colors">{page.title}</div>
                <div className="text-xs sm:text-sm text-slate-500 mt-1">{page.description}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-display font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.slice(0, 6).map((step, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                  {index + 1}
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-display font-bold text-center mb-4">Why Contractors Love LetWise</h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Everything you need to grow your trade business
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="p-3 bg-orange-100 rounded-lg w-fit mb-4">
                    <Icon className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="font-bold mb-2">{benefit.title}</h3>
                  <p className="text-slate-600 text-sm">{benefit.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-display font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="p-6">
                <h3 className="font-bold text-lg mb-2">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Card className="p-12 text-center bg-gradient-to-r from-orange-600 to-amber-600 text-white">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-white/20 flex items-center justify-center">
              <Wrench className="w-8 h-8" />
            </div>
            <h2 className="text-3xl font-display font-bold mb-4">Ready to grow your business?</h2>
            <p className="text-xl mb-8 opacity-90">Apply to join our contractor network and start receiving jobs.</p>
            <Link to="/contractor/register">
              <Button size="lg" className="bg-white text-orange-600 hover:bg-slate-100">
                Apply Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </Card>
        </div>
      </section>
    </PublicLayout>
  );
}
